/**
 * 온습도로거 '현황' 탭 — 하우스 20여 대를 한 화면에서 본다.
 *
 * 설계 (2026-11-04 재검토):
 *  · 사용자의 첫 질문은 '지금 각 하우스가 몇 도인가 · 이상 없는가' 다. 장치 대장(관리 표)이나
 *    측정 데이터(한 대씩 고르는 차트)로는 20대를 비교할 수 없어 카드 격자를 첫 탭으로 둔다.
 *  · 카드 1장 = 하우스 1동: 현재 온도·습도(가장 큰 글자) → 24시간 추이 미니 그래프 → 24시간 최저·최고 → 상태.
 *  · '값이 안 온다'를 '값이 나쁘다'보다 먼저 보인다 — 수신 지연·미수신 카드는 테두리·머리띠를 바꾸고
 *    값 옆에 '마지막 값' 과 경과 시간을 적는다(오래된 값을 현재 값처럼 읽지 않게).
 *  · 색만으로 구분하지 않는다 — 상태는 글자 태그, 온도/습도는 단위와 라벨을 함께 쓴다.
 *  · 미니 그래프는 SVG 직접 그리기(장치 수만큼 ECharts 인스턴스를 만들지 않는다). 측정이 빈 시간은 선을 끊는다.
 *  · 데이터는 /api/thlogger/overview 한 번(쿼리 2개)으로 받는다 — 장치 수만큼 /readings 를 부르지 않는다.
 *  · 카드는 **설치 지점** 단위다(지점이 없으면 장치 단위로 대체). 기기를 바꿔도 같은 카드 · 같은 추이가 이어지고,
 *    지금 설치된 기기는 카드 아래에 작게 적는다. 기기가 없는 지점은 '기기 없음 — 기록 중단' 으로 드러낸다.
 *  · 배치 편집(쓰기 권한): 카드를 끌어 현장 배치처럼 놓고 카드 배경색으로 구역 · 작물을 묶는다. 순서 · 색은 서버(지점)에
 *    저장해 **모두가 같은 배치도**를 본다. 카드 크기 · 표시 항목은 사람마다 달라 브라우저에만 저장한다.
 *    배경색은 연한 색만 고른다 — 상태(수신 지연 · 미수신)는 테두리 · 머리띠 · 글자 태그가 맡고 배경과 겹치지 않는다.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Input, Segmented, Select, Space, Tag, Tooltip, Typography, Button, message, Popover, Checkbox, Alert } from 'antd';
import { SearchOutlined, ReloadOutlined, WarningOutlined, LineChartOutlined, DragOutlined, SettingOutlined, SaveOutlined, CloseOutlined, HolderOutlined } from '@ant-design/icons';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { usePermission } from '../contexts/PermissionContext';
import dayjs from 'dayjs';
import { thloggerAPI } from '../services/api';
import { gray, state } from '../theme/tokens';
import { fmtDateTime } from '../utils/date';
import { useFillHeight } from '../hooks/useViewportFit';
import { EmptyState } from './common';
import { BatteryBadge, SignalBadge } from './ThLoggerBadges';
import { devIssues, summarize, type Issue } from './ThLoggerReception';

const { Text } = Typography;
const T_COLOR = '#e5484d';   // 온도 — 측정 데이터 차트와 같은 색
const H_COLOR = '#2a78d6';   // 습도

const LEVEL: Record<string, { label: string; color: string; bg: string; band: string }> = {
  OK:   { label: '수신 정상', color: state.successText, bg: state.successSoft, band: 'transparent' },
  WARN: { label: '수신 지연', color: state.warningText, bg: state.warningSoft, band: '#eda100' },
  CRIT: { label: '미수신',   color: state.errorText,   bg: state.errorSoft,   band: '#e5484d' },
  NEW:  { label: '수신 대기', color: gray[600],         bg: gray[100],         band: '#94a3b8' },
};

type Pt = [string, number | null, number | null];

/* 카드 배경 팔레트 — 연한 색만(글자 대비 유지). 이름은 고를 때 툴팁으로 보인다 */
export const CARD_COLORS: { v: string; n: string }[] = [
  { v: '', n: '없음(흰색)' }, { v: '#eef2ff', n: '인디고' }, { v: '#e0f2fe', n: '하늘' }, { v: '#ecfdf5', n: '초록' },
  { v: '#fef9c3', n: '노랑' }, { v: '#ffedd5', n: '주황' }, { v: '#fce7f3', n: '분홍' }, { v: '#f3e8ff', n: '보라' }, { v: '#f1f5f9', n: '회색' },
];
/* 타이틀 바 팔레트 — 진한 색 위에 흰 글자(대비 4.5 이상인 색만) */
export const BAR_COLORS: { v: string; n: string }[] = [
  { v: '', n: '없음' }, { v: '#4f46e5', n: '인디고' }, { v: '#2563eb', n: '파랑' }, { v: '#0f766e', n: '청록' }, { v: '#15803d', n: '초록' },
  { v: '#b45309', n: '황토' }, { v: '#c2410c', n: '주황' }, { v: '#be123c', n: '장미' }, { v: '#7e22ce', n: '보라' }, { v: '#475569', n: '회청' },
];
export const CARD_SIZES: { v: 'S' | 'M' | 'L'; n: string; d: string }[] = [
  { v: 'S', n: '작게', d: '값 위주 — 미니 그래프를 낮게' }, { v: 'M', n: '보통', d: '기본' }, { v: 'L', n: '크게', d: '가로 2칸 · 큰 그래프 — 중요한 지점' },
];

/* 색 · 크기 고르기 — 현황 배치 편집 · 장치 대장 · 설치 지점이 같은 부품을 쓴다 */
const Swatches: React.FC<{ list: { v: string; n: string }[]; value?: string; onPick: (v: string) => void; label: string }> = ({ list, value, onPick, label }) => (
  <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }} onPointerDown={ev => ev.stopPropagation()} onClick={ev => ev.stopPropagation()}>
    <span style={{ fontSize: 11, color: gray[600], width: 34, flex: 'none' }}>{label}</span>
    {list.map(c => (
      <Tooltip key={c.v || 'none'} title={c.n}>
        <button type="button" aria-label={`${label} ${c.n}`} onClick={() => onPick(c.v)}
          style={{ width: 18, height: 18, borderRadius: 9, cursor: 'pointer', padding: 0, background: c.v || '#fff',
            border: (value || '') === c.v ? '2px solid #111827' : `1px solid ${gray[300]}`, boxShadow: (value || '') === c.v ? '0 0 0 2px #fff inset' : undefined,
            backgroundImage: c.v ? undefined : 'linear-gradient(135deg, transparent 45%, #e5484d 45%, #e5484d 55%, transparent 55%)' }} />
      </Tooltip>))}
  </div>
);
const SizePick: React.FC<{ value?: string; onPick: (v: string) => void }> = ({ value, onPick }) => (
  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }} onPointerDown={ev => ev.stopPropagation()} onClick={ev => ev.stopPropagation()}>
    <span style={{ fontSize: 11, color: gray[600], width: 34, flex: 'none' }}>크기</span>
    <Segmented size="small" value={value || 'M'} onChange={v => onPick(String(v))}
      options={CARD_SIZES.map(z => ({ value: z.v, label: <Tooltip title={z.d}><span>{z.n}</span></Tooltip> }))} />
  </div>
);

/* 장치 대장 · 설치 지점의 '현황 카드' 열 — 미리보기 칩을 누르면 배경 · 타이틀 바 · 크기를 고르고 바로 저장한다 */
export const CardStylePicker: React.FC<{ point?: { id: number; code?: string; color?: string; bar_color?: string; card_size?: string } | null; canWrite: boolean; onSaved: () => void }> = ({ point, canWrite, onSaved }) => {
  const [cur, setCur] = useState({ color: point?.color || '', bar_color: point?.bar_color || '', card_size: point?.card_size || '' });
  useEffect(() => { setCur({ color: point?.color || '', bar_color: point?.bar_color || '', card_size: point?.card_size || '' }); }, [point?.color, point?.bar_color, point?.card_size]);
  if (!point) return <Tooltip title="설치 지점이 없어 현황 카드가 없습니다 — 설치 지점 탭에서 설치"><span style={{ fontSize: 11.5, color: gray[400] }}>카드 없음</span></Tooltip>;
  const save = async (patch: Partial<typeof cur>) => {
    const next = { ...cur, ...patch };
    setCur(next);
    try { await thloggerAPI.updatePoint(point.id, patch); onSaved(); } catch (e: any) { message.error(e?.response?.data?.detail || '저장에 실패했습니다.'); }
  };
  const chip = (
    <span style={{ display: 'inline-flex', flexDirection: 'column', width: 54, height: 30, borderRadius: 5, overflow: 'hidden', border: `1px solid ${gray[300]}`,
      background: cur.color || '#fff', cursor: canWrite ? 'pointer' : 'default', verticalAlign: 'middle' }}>
      <span style={{ height: 10, background: cur.bar_color || gray[100] }} />
      <span style={{ fontSize: 11, lineHeight: '18px', textAlign: 'center', color: gray[700], fontWeight: 700 }}>{(CARD_SIZES.find(z => z.v === (cur.card_size || 'M'))?.n)}</span>
    </span>);
  if (!canWrite) return chip;
  return (
    <Popover trigger="click" placement="left" title={`현황 카드 — ${point.code || ''} (지점에 저장 · 기기를 바꿔도 유지)`} content={
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: 280 }}>
        <Swatches list={BAR_COLORS} value={cur.bar_color} onPick={v => save({ bar_color: v })} label="타이틀" />
        <Swatches list={CARD_COLORS} value={cur.color} onPick={v => save({ color: v })} label="배경" />
        <SizePick value={cur.card_size} onPick={v => save({ card_size: v })} />
        <div style={{ fontSize: 11, color: gray[500] }}>모든 사용자의 현황 화면에 같은 색 · 크기로 보입니다. 순서는 현황 '배치 편집' 에서 끌어 바꿉니다.</div>
      </div>}>
      <Tooltip title="현황 카드 색 · 크기">{chip}</Tooltip>
    </Popover>
  );
};

type View = { size: 'normal' | 'compact'; spark: boolean; range: boolean; badges: boolean; assign: boolean };
const VIEW_DEFAULT: View = { size: 'normal', spark: true, range: true, badges: true, assign: true };
const VIEW_KEY = 'sappt_thl_ov_view';
const loadView = (): View => { try { return { ...VIEW_DEFAULT, ...JSON.parse(localStorage.getItem(VIEW_KEY) || '{}') }; } catch { return VIEW_DEFAULT; } };

/* 업로드 직후가 아니면 '지금' 값은 마지막 측정이다 — 경과를 사람이 읽는 말로 */
const ago = (ts?: string | null) => {
  if (!ts) return '-';
  const m = dayjs().diff(dayjs(ts), 'minute');
  if (m < 60) return `${Math.max(m, 0)}분 전`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h}시간 전` : `${Math.floor(h / 24)}일 전`;
};

/* 미니 그래프 — 온도(왼쪽 범위)·습도(오른쪽 범위)를 각자 정규화. 1시간 넘게 빈 칸은 선을 끊는다 */
const Spark: React.FC<{ pts: Pt[]; hours: number; now: string; h?: number; tinted?: boolean }> = ({ pts, hours, now, h: hh, tinted }) => {
  const W = 240, H = hh || 56, P = 3;
  const end = dayjs(now).valueOf();
  const start = end - hours * 3600_000;
  const x = (t: number) => P + ((t - start) / (end - start)) * (W - 2 * P);
  const path = (idx: 1 | 2) => {
    const vals = pts.map(p => p[idx]).filter((v): v is number => v != null);
    if (!vals.length) return { d: '', lo: null as number | null, hi: null as number | null };
    let lo = Math.min(...vals), hi = Math.max(...vals);
    if (hi - lo < (idx === 1 ? 2 : 6)) { const mid = (hi + lo) / 2; const half = idx === 1 ? 1 : 3; lo = mid - half; hi = mid + half; }
    const y = (v: number) => P + (1 - (v - lo) / (hi - lo)) * (H - 2 * P);
    let d = '', prev: number | null = null;
    for (const p of pts) {
      const v = p[idx]; const t = dayjs(p[0]).valueOf();
      if (v == null) { prev = null; continue; }
      d += `${prev == null || t - prev > 90 * 60_000 ? 'M' : 'L'}${x(t).toFixed(1)},${y(v).toFixed(1)} `;
      prev = t;
    }
    return { d, lo, hi };
  };
  const t = path(1), h = path(2);
  // 자정 눈금 — 어제/오늘 경계를 읽게 한다
  const midnights: number[] = [];
  for (let m = dayjs(start).add(1, 'day').startOf('day'); m.valueOf() < end; m = m.add(1, 'day')) midnights.push(m.valueOf());
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
      aria-label={`최근 ${hours}시간 온도·습도 추이`} style={{ display: 'block' }}>
      <rect x={0} y={0} width={W} height={H} rx={4} fill={tinted ? 'rgba(255,255,255,.65)' : gray[50]} />
      {midnights.map(m => <line key={m} x1={x(m)} x2={x(m)} y1={2} y2={H - 2} stroke={gray[200]} strokeDasharray="2 2" vectorEffect="non-scaling-stroke" />)}
      {h.d && <path d={h.d} fill="none" stroke={H_COLOR} strokeWidth={1.4} strokeOpacity={0.75} vectorEffect="non-scaling-stroke" />}
      {t.d && <path d={t.d} fill="none" stroke={T_COLOR} strokeWidth={1.8} vectorEffect="non-scaling-stroke" />}
      {!t.d && <text x={W / 2} y={H / 2 + 4} textAnchor="middle" fontSize={11} fill={gray[400]}>최근 {hours}시간 측정 없음</text>}
    </svg>
  );
};

const fmt1 = (v: any) => (v == null ? '-' : Number(v).toFixed(1));

/* 카드 한 장의 재료 — 지점이면 지점 정보 + 지금 설치된 기기, 장치 모드면 장치 자신 */
type Entry = { key: number; code?: string; title: string; position?: string; d: any; a: any; noDevice: boolean; devLabel?: string; color?: string; bar?: string; size?: string };
const NODEV = { label: '기기 없음', color: gray[600], bg: gray[100], band: '#94a3b8' };

type CardProps = { e: Entry; ov: any; hours: number; now: string; onOpen: (id: number) => void; view: View;
  editing?: boolean; color?: string; bar?: string; size?: string; issue?: Issue;
  onColor?: (c: string) => void; onBar?: (c: string) => void; onSize?: (z: string) => void; drag?: any };
const DeviceCard: React.FC<CardProps> = ({ e, ov, hours, now, onOpen, view, editing, color, bar, size, onColor, onBar, onSize, drag, issue }) => {
  const d = e.d || {};
  const bg = color || '#fff';
  // 카드별 크기(지점에 저장) 가 우선, 없으면 표시 설정의 기본 크기
  const z = size || (view.size === 'compact' ? 'S' : 'M');
  const compact = z === 'S';
  const large = z === 'L';
  const lv = e.noDevice ? NODEV : (LEVEL[d.level] || LEVEL.NEW);
  const stale = e.noDevice || d.level === 'WARN' || d.level === 'CRIT';
  const s = ov?.stats24;
  const a = e.a;
  // 이상 이유 — 장치 대장과 같은 판정(devIssues, 최근 14일 누락 · 지연 포함)
  const issues: string[] = issue?.reasons || [];
  return (
    <div onClick={() => !editing && onOpen(e.key)} role="button" tabIndex={0} onKeyDown={ev => { if (ev.key === 'Enter' && !editing) onOpen(e.key); }}
      className={`thl-card${editing ? ' editing' : ''}`} ref={drag?.setNodeRef} {...(drag?.attributes || {})}
      style={{
        position: 'relative', background: bg, borderRadius: 10, cursor: editing ? 'grab' : 'pointer', overflow: 'hidden',
        border: `1px solid ${stale ? lv.band : color ? 'rgba(15,23,42,.10)' : gray[200]}`, boxShadow: stale ? `0 0 0 1px ${lv.band}33` : undefined,
        display: 'flex', flexDirection: 'column', touchAction: editing ? 'none' : undefined, gridColumn: large ? 'span 2' : undefined,
        transform: drag ? CSS.Transform.toString(drag.transform) : undefined, transition: drag?.transition,
        zIndex: drag?.isDragging ? 5 : undefined, opacity: drag?.isDragging ? 0.85 : 1,
      }} {...(editing ? (drag?.listeners || {}) : {})}>
      <div style={{ height: 4, background: lv.band }} />
      {/* 머리(타이틀 바): 지점 코드 · 이름 · 수신 상태 — 타이틀 바 색을 지정하면 진한 바탕 + 흰 글자 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, background: bar || undefined,
        padding: bar ? (compact ? '5px 10px' : '7px 12px') : (compact ? '6px 10px 0' : '8px 12px 0') }}>
        {editing && <HolderOutlined style={{ color: bar ? 'rgba(255,255,255,.85)' : gray[500], flex: 'none' }} />}
        {e.code && <span style={{ fontSize: 11, fontWeight: 700, color: bar ? '#fff' : gray[700], background: bar ? 'rgba(255,255,255,.2)' : color ? 'rgba(255,255,255,.75)' : gray[100], borderRadius: 4, padding: '0 6px', lineHeight: '18px', flex: 'none' }}>{e.code}</span>}
        <b style={{ fontSize: large ? 15 : 13.5, color: bar ? '#fff' : undefined, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }} title={e.position}>{e.title}</b>
        <Tag style={{ margin: 0, color: lv.color, background: lv.bg, borderColor: lv.bg, fontWeight: 700, flex: 'none' }}>{lv.label}</Tag>
      </div>
      <div style={{ padding: compact ? '4px 10px 8px' : '6px 12px 10px', display: 'flex', flexDirection: 'column', gap: compact ? 4 : 6, flex: 1 }}>
        {view.assign && <div style={{ fontSize: 11.5, color: a ? gray[600] : gray[400], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={a ? `${a.test_number} ${a.schedule_title || ''}` : undefined}>
          {a ? <>{a.test_number}{a.schedule_title ? ` · ${a.schedule_title}` : ''}</> : '배정된 시험 없음'}
        </div>}
        {/* 현재 값 — 가장 큰 글자 */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, opacity: stale ? 0.55 : 1 }}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            <span style={{ fontSize: 11, color: T_COLOR, fontWeight: 700, marginRight: 4 }}>온도</span>
            <b style={{ fontSize: compact ? 21 : large ? 32 : 26, lineHeight: 1 }}>{fmt1(d.last_temp_c)}</b><span style={{ fontSize: 13, color: gray[500], marginLeft: 1 }}>℃</span>
          </span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            <span style={{ fontSize: 11, color: H_COLOR, fontWeight: 700, marginRight: 4 }}>습도</span>
            <b style={{ fontSize: compact ? 21 : large ? 32 : 26, lineHeight: 1 }}>{fmt1(d.last_humidity)}</b><span style={{ fontSize: 13, color: gray[500], marginLeft: 1 }}>%</span>
          </span>
        </div>
        <div style={{ fontSize: 11.5, color: stale ? lv.color : gray[500], fontWeight: stale ? 600 : 400 }}>
          {e.noDevice ? '기기 없음 — 이 지점의 기록이 멈춰 있습니다' : d.last_reading_ts
            ? <Tooltip title={`마지막 측정 ${fmtDateTime(d.last_reading_ts)} · 마지막 수신 ${fmtDateTime(d.last_uplink_at)}`}>
                {stale ? '마지막 값 · ' : '측정 '}{dayjs(d.last_reading_ts).format('MM-DD HH:mm')} ({ago(d.last_reading_ts)})
              </Tooltip>
            : '아직 측정값이 없습니다'}
        </div>
        {view.spark && <Spark pts={ov?.series || []} hours={hours} now={now} h={compact ? 34 : large ? 96 : 56} tinted={!!color} />}
        {/* 24시간 범위 */}
        {view.range && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 11.5, color: gray[600], fontVariantNumeric: 'tabular-nums' }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 2, background: T_COLOR, verticalAlign: 'middle', marginRight: 4 }} />
            24h {fmt1(s?.temp_min)} ~ <b style={{ color: gray[800] }}>{fmt1(s?.temp_max)}</b>℃</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 2, background: H_COLOR, verticalAlign: 'middle', marginRight: 4 }} />
            24h {fmt1(s?.hum_min)} ~ <b style={{ color: gray[800] }}>{fmt1(s?.hum_max)}</b>%</span>
        </div>}
        {view.badges && <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginTop: 'auto' }}>
          {!e.noDevice && <BatteryBadge v={d.last_battery_v} compact />}
          {!e.noDevice && <SignalBadge dbm={d.last_signal_dbm} compact />}
          {e.devLabel && <span style={{ fontSize: 11, color: gray[400], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }} title={e.devLabel}>{e.devLabel}</span>}
          {issues.length > 0 && <Tooltip title={issues.join(' · ')}><WarningOutlined style={{ color: state.warningText, marginLeft: 'auto' }} /></Tooltip>}
        </div>}
        {editing && onColor && (
          <div onPointerDown={ev => ev.stopPropagation()} onClick={ev => ev.stopPropagation()}
            style={{ display: 'flex', flexDirection: 'column', gap: 5, paddingTop: 6, borderTop: `1px dashed ${gray[300]}`, cursor: 'default' }}>
            {onBar && <Swatches list={BAR_COLORS} value={bar} onPick={onBar} label="타이틀" />}
            <Swatches list={CARD_COLORS} value={color} onPick={onColor} label="배경" />
            {onSize && <SizePick value={size} onPick={onSize} />}
          </div>)}
      </div>
    </div>
  );
};

/* 배치 편집 — dnd-kit 정렬 래퍼 (MasterPage · 역할 관리와 같은 라이브러리) */
const SortableCard: React.FC<CardProps> = props => {
  const drag = useSortable({ id: props.e.key });
  return <DeviceCard {...props} drag={drag} />;
};

const SORTS = [
  { value: 'layout', label: '배치 순' },
  { value: 'house', label: '지점 코드 순' },
  { value: 'attention', label: '확인 필요 먼저' },
  { value: 'temp_desc', label: '온도 높은 순' },
  { value: 'temp_asc', label: '온도 낮은 순' },
];
const LEVEL_RANK: Record<string, number> = { CRIT: 0, WARN: 1, NEW: 2, OK: 3 };

/* '확인 필요' = 기기 없음 또는 장치 대장의 이상 판정(devIssues)과 같은 기준 */
type RxData = { now: string; items: Record<string, any[]> } | null;
const issueOf = (e: Entry, rx: RxData): Issue => (e.noDevice || !e.d ? { lv: null, reasons: [] }
  : devIssues(e.d, rx ? summarize(rx.items?.[e.d.id], e.d.tdc_sec, rx.now, 14) : null));
const needsAttention = (e: Entry, rx: RxData) => e.noDevice || !!issueOf(e, rx).lv;

const ThLoggerOverview: React.FC<{ points: any[]; devices: any[]; onOpen: (kind: 'point' | 'device', id: number) => void; reload?: () => void }> = ({ points, devices, onOpen, reload }) => {
  const { ref, height } = useFillHeight(300);
  const { hasPermission } = usePermission();
  const canWrite = hasPermission('thlogger', 'WRITE');
  const [view, setView] = useState<View>(loadView);
  useEffect(() => { try { localStorage.setItem(VIEW_KEY, JSON.stringify(view)); } catch { /* 저장 불가 환경 */ } }, [view]);
  const [editing, setEditing] = useState(false);
  const [order, setOrder] = useState<number[]>([]);
  const [colors, setColors] = useState<Record<number, string>>({});
  const [bars, setBars] = useState<Record<number, string>>({});
  const [sizes, setSizes] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [hours, setHours] = useState<number>(() => { try { return Number(localStorage.getItem('sappt_thl_ov_hours')) || 48; } catch { return 48; } });
  const [ov, setOv] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<'all' | 'attention'>('all');
  const [sort, setSort] = useState('layout');

  const [rx, setRx] = useState<RxData>(null);
  const load = useCallback(() => {
    setLoading(true);
    thloggerAPI.uplinkHistory(14).then(r => setRx(r.data)).catch(() => {});
    thloggerAPI.overview(hours).then(r => setOv(r.data)).catch(() => message.error('현황을 불러오지 못했습니다.')).finally(() => setLoading(false));
  }, [hours]);
  useEffect(() => { load(); const t = setInterval(load, 300000); return () => clearInterval(t); }, [load]);
  useEffect(() => { try { localStorage.setItem('sappt_thl_ov_hours', String(hours)); } catch { /* 저장 불가 환경 */ } }, [hours]);

  const byId = useMemo(() => Object.fromEntries((ov?.items || []).map((x: any) => [x.id, x])), [ov]);
  const usePoints = points.some(p => p.is_active);
  const active: Entry[] = useMemo(() => usePoints
    ? points.filter(p => p.is_active).map(p => ({ key: p.id, code: p.code, title: p.name, position: p.position, d: p.device, a: p.current_assignment,
        noDevice: !p.current, devLabel: p.current ? `${p.current.device_name}` : undefined, color: p.color || '', bar: p.bar_color || '', size: p.card_size || '' }))
    : devices.filter(d => d.is_active).map(d => ({ key: d.id, code: d.house_code, title: d.name, d, a: d.current_assignment, noDevice: false })),
  [usePoints, points, devices]);
  const attentionN = active.filter(e => needsAttention(e, rx)).length;
  const list = useMemo(() => {
    const kw = q.trim().toLowerCase();
    let l = active.filter(e => !kw || [e.title, e.code, e.position, e.devLabel, e.d?.imei, e.a?.test_number, e.a?.schedule_title]
      .some(v => v && String(v).toLowerCase().includes(kw)));
    if (scope === 'attention') l = l.filter(e => needsAttention(e, rx));
    const house = (a: Entry, b: Entry) => String(a.code || a.title).localeCompare(String(b.code || b.title), 'ko', { numeric: true });
    const tv = (e: Entry) => (e.noDevice || e.d?.last_temp_c == null ? null : Number(e.d.last_temp_c));
    return [...l].sort((a, b) => {
      if (sort === 'attention') return (Number(needsAttention(b, rx)) - Number(needsAttention(a, rx))) || ((LEVEL_RANK[a.d?.level] ?? -1) - (LEVEL_RANK[b.d?.level] ?? -1)) || house(a, b);
      if (sort === 'temp_desc' || sort === 'temp_asc') {
        const x = tv(a), y = tv(b);
        if (x == null || y == null) return x == null ? 1 : -1;
        return sort === 'temp_desc' ? y - x : x - y;
      }
      return sort === 'house' ? house(a, b) : 0;   // 배치 순 = 서버 순서(sort_order) 그대로 — 안정 정렬이라 0 이면 유지
    });
  }, [active, q, scope, sort, rx]);

  const startEdit = () => {
    setOrder(active.map(e => e.key)); setColors(Object.fromEntries(active.map(e => [e.key, e.color || ''])));
    setBars(Object.fromEntries(active.map(e => [e.key, e.bar || '']))); setSizes(Object.fromEntries(active.map(e => [e.key, e.size || ''])));
    setScope('all'); setQ(''); setEditing(true);
  };
  // 한꺼번에 — 선택 없이 전체 카드에 같은 크기 · 타이틀 바를 줄 때
  const allSize = (z: string) => setSizes(Object.fromEntries(order.map(k => [k, z])));
  const editList = useMemo(() => order.map(k => active.find(e => e.key === k)).filter(Boolean) as Entry[], [order, active]);
  const onDragEnd = (ev: DragEndEvent) => {
    const { active: a, over } = ev;
    if (!over || a.id === over.id) return;
    setOrder(o => arrayMove(o, o.indexOf(Number(a.id)), o.indexOf(Number(over.id))));
  };
  const saveLayout = async () => {
    setSaving(true);
    try {
      await thloggerAPI.savePointLayout({ ids: order, colors: Object.fromEntries(Object.entries(colors).map(([k, v]) => [k, v || ''])),
        bars: Object.fromEntries(Object.entries(bars).map(([k, v]) => [k, v || ''])), sizes: Object.fromEntries(Object.entries(sizes).map(([k, v]) => [k, v || ''])) });
      message.success('배치를 저장했습니다. 모든 사용자에게 같은 배치로 보입니다.');
      setEditing(false); setSort('layout'); reload?.();
    } catch (e: any) { message.error(e?.response?.data?.detail || '배치 저장에 실패했습니다.'); } finally { setSaving(false); }
  };

  // 타이틀 바 범례 — 같은 바 색을 쓰는 지점을 묶어 코드 범위로 보여 준다(색의 뜻을 화면에서 읽게). 이름은 없으므로 범위로 말한다
  const barLegend = useMemo(() => {
    const g: Record<string, string[]> = {};
    active.forEach(e => { if (e.bar) (g[e.bar] = g[e.bar] || []).push(e.code || e.title); });
    return Object.entries(g).map(([c, codes]) => {
      const sorted = [...codes].sort((a, b) => a.localeCompare(b, 'ko', { numeric: true }));
      return { c, n: BAR_COLORS.find(x => x.v === c)?.n || '', label: sorted.length > 2 ? `${sorted[0]} ~ ${sorted[sorted.length - 1]}` : sorted.join(' · '), cnt: sorted.length };
    }).sort((a, b) => a.label.localeCompare(b.label, 'ko', { numeric: true }));
  }, [active]);
  const largeN = active.filter(e => e.size === 'L').length;

  // 전체 하우스 온도 범위 — 한 줄 요약 (가장 더운/추운 하우스를 바로 읽게)
  const temps = active.filter(e => !e.noDevice && e.d?.last_temp_c != null && e.d?.level === 'OK');
  const hot = temps.length ? temps.reduce((m, e) => (e.d.last_temp_c > m.d.last_temp_c ? e : m)) : null;
  const cold = temps.length ? temps.reduce((m, e) => (e.d.last_temp_c < m.d.last_temp_c ? e : m)) : null;

  if (!active.length) return <EmptyState title="설치 지점이 없습니다" description="설치 지점 탭에서 지점을 등록하고 기기를 설치하면 여기에 지점별 카드가 생깁니다." />;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <Input size="small" allowClear prefix={<SearchOutlined style={{ color: gray[400] }} />} placeholder="하우스 · 장치 · 시험번호"
          value={q} onChange={e => setQ(e.target.value)} style={{ width: 210 }} />
        <Segmented size="small" value={scope} onChange={v => setScope(v as any)}
          options={[{ value: 'all', label: `전체 ${active.length}` }, { value: 'attention', label: <span><WarningOutlined /> 확인 필요 {attentionN}</span> }]} />
        <Select size="small" value={sort} onChange={setSort} options={SORTS} style={{ width: 132 }} />
        <Segmented size="small" value={hours} onChange={v => setHours(Number(v))}
          options={[{ value: 24, label: '24시간' }, { value: 48, label: '48시간' }, { value: 168, label: '7일' }]} />
        <Space size={10} style={{ marginLeft: 'auto', fontSize: 12, color: gray[600] }} wrap>
          {hot && cold && hot.key !== cold.key && <span>
            최고 <b style={{ color: gray[800] }}>{hot.code || hot.title} {fmt1(hot.d.last_temp_c)}℃</b> · 최저 <b style={{ color: gray[800] }}>{cold.code || cold.title} {fmt1(cold.d.last_temp_c)}℃</b>
          </span>}
          <span><span style={{ display: 'inline-block', width: 12, height: 2, background: T_COLOR, verticalAlign: 'middle', marginRight: 4 }} />온도
            <span style={{ display: 'inline-block', width: 12, height: 2, background: H_COLOR, verticalAlign: 'middle', margin: '0 4px 0 10px' }} />습도 · 점선 = 자정</span>
          <Popover trigger="click" placement="bottomRight" title="카드 표시 (이 브라우저에만 저장)" content={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: 210 }}>
              <div style={{ fontSize: 12, color: gray[600] }}>기본 크기 <span style={{ color: gray[400] }}>(카드별 크기를 정하지 않은 카드)</span></div>
              <Segmented size="small" block value={view.size} onChange={v => setView(x => ({ ...x, size: v as any }))}
                options={[{ value: 'normal', label: '보통' }, { value: 'compact', label: '작게' }]} />
              {([['spark', '추이 미니 그래프'], ['range', '24시간 최저 · 최고'], ['badges', '배터리 · 신호 · 기기'], ['assign', '배정 시험']] as [keyof View, string][]).map(([k, l]) => (
                <Checkbox key={k} checked={!!view[k]} onChange={ev => setView(x => ({ ...x, [k]: ev.target.checked }))}>{l}</Checkbox>))}
              <Button size="small" type="link" style={{ padding: 0, alignSelf: 'flex-start' }} onClick={() => setView(VIEW_DEFAULT)}>기본값으로</Button>
            </div>}>
            <Tooltip title="카드 크기 · 표시 항목"><Button size="small" icon={<SettingOutlined />} /></Tooltip>
          </Popover>
          {canWrite && usePoints && !editing && <Button size="small" icon={<DragOutlined />} onClick={startEdit}>배치 편집</Button>}
          <Tooltip title="새로고침 (5분마다 자동)"><Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={load} /></Tooltip>
        </Space>
      </div>
      {!editing && (barLegend.length > 0 || largeN > 0) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontSize: 12, color: gray[600], margin: '-2px 0 8px' }}>
          {barLegend.length > 0 && <span style={{ color: gray[500] }}>타이틀 바</span>}
          {barLegend.map(x => (
            <span key={x.c} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 14, height: 10, borderRadius: 3, background: x.c }} />{x.label}<span style={{ color: gray[400] }}>({x.cnt})</span>
            </span>))}
          {largeN > 0 && <span style={{ color: gray[500] }}>· 큰 카드 {largeN}곳</span>}
          {canWrite && <span style={{ color: gray[400] }}>— 색 · 크기 · 위치는 '배치 편집' 또는 장치 대장 '현황 카드' 열에서 바꿉니다</span>}
        </div>)}
      {editing && (
        <Alert type="info" showIcon style={{ marginBottom: 8, padding: '6px 12px' }}
          message={<span><b>배치 편집</b> — 카드를 끌어 현장 배치처럼 놓고, 카드 아래에서 타이틀 바 · 배경색 · 크기(작게 · 보통 · 크게=가로 2칸)를 고르세요. 저장하면 모든 사용자에게 같은 배치로 보입니다.</span>}
          action={<Space size={6}>
            <span style={{ fontSize: 12, color: gray[600] }}>전체 크기</span>
            <Segmented size="small" onChange={v => allSize(String(v))} options={CARD_SIZES.map(z => ({ value: z.v, label: z.n }))} />
            <Button size="small" icon={<CloseOutlined />} onClick={() => setEditing(false)}>취소</Button>
            <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveLayout}>저장</Button>
          </Space>} />)}
      <div ref={ref} style={{ height, overflowY: 'auto', overflowX: 'hidden', paddingRight: 2 }}>
        {editing ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={order} strategy={rectSortingStrategy}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gridAutoFlow: 'dense', gap: 10 }}>
                {editList.map(e => <SortableCard key={e.key} e={e} ov={ov?.by === 'point' ? byId[e.key] : undefined} hours={hours} view={view}
                  now={ov?.now || new Date().toISOString()} onOpen={() => {}} editing color={colors[e.key]} bar={bars[e.key]} size={sizes[e.key]}
                  onColor={c => setColors(x => ({ ...x, [e.key]: c }))} onBar={c => setBars(x => ({ ...x, [e.key]: c }))} onSize={zz => setSizes(x => ({ ...x, [e.key]: zz }))} />)}
              </div>
            </SortableContext>
          </DndContext>
        ) : !list.length ? <EmptyState title={scope === 'attention' ? '확인이 필요한 장치가 없습니다' : '조건에 맞는 장치가 없습니다'} size="sm" /> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gridAutoFlow: 'dense', gap: 10 }}>
            {list.map(e => <DeviceCard key={e.key} e={e} ov={ov?.by === (usePoints ? 'point' : 'device') ? byId[e.key] : undefined} hours={hours} view={view}
              color={e.color} bar={e.bar} size={e.size} issue={issueOf(e, rx)} now={ov?.now || new Date().toISOString()} onOpen={id => onOpen(usePoints ? 'point' : 'device', id)} />)}
          </div>
        )}
        <div style={{ fontSize: 11.5, color: gray[400], marginTop: 8 }}>
          <LineChartOutlined /> 카드를 누르면 그 지점의 측정 데이터(차트 · 통계 · 엑셀, 기기 교체 경계 표시)로 이동합니다. 미니 그래프는 1시간 평균이며 측정이 빈 시간은 선이 끊깁니다.
          <Text type="secondary" style={{ fontSize: 11.5, marginLeft: 6 }}>수신 지연 · 미수신 카드의 값은 마지막으로 받은 값입니다.</Text>
        </div>
      </div>
    </div>
  );
};

export default ThLoggerOverview;
